
import logger from '../app/logger';
import { EXTEND_CONFERENCE_EXPIRY,
    IS_CUSTOM_PARTICIPANT_EVERYONE,
    MODERATOR_UNLOCK_AUDIO,
    MODERATOR_UNLOCK_VIDEO,
    getCurrentConference,
    moderatorunclockAudio,
    moderatorunclockVideo,
    setCustomParticipantEveryone } from '../base/conference';
import { MHConferenceEvents } from '../base/lib-meet-hour';
import { VIDEO_TYPE } from '../base/media';
import {
    PARTICIPANT_COMPANY,
    PARTICIPANT_DESIGNATION,
    PIN_PARTICIPANT,
    SET_MODERATOR_PIN,
    participantDesignation,
    participantOrganization,
    pinParticipant,
    setModeratorPin
} from '../base/participants';
import { StateListenerRegistry } from '../base/redux';
import { checkIfReactNative } from '../base/util/checkOS';
import { DELETE_MESSAGE, RESTORE_MESSAGE } from '../chat';
import { openChat, openPolls, setIsPollsTabFocused, setPrivateChatDisabled } from '../chat/actions';
import { deleteChatMessage, restoreChatMessage } from '../chat/actions.any';
import { CONFERENCE_SUBJECT } from '../conference/actionTypes';
import { conferenceSubjectHandler } from '../conference/actions';
import { SET_FOCUS_MODE_MESSAGE_TYPE } from '../focus-mode/constants';
import { selectParticipantInLargeVideo } from '../large-video/actions.any';
import { NOTIFICATION_TIMEOUT_TYPE, NOTIFICATION_TYPE, showNotification } from '../notifications';
import { close } from '../participants-pane/actions';
import { normalizeMessageSource } from '../visitors/functions';
import {
    IDENTITY_ZOOM_STATE,
    getRemoteVideoZoom,
    isSameZoomState,
    parseZoomCommand,
    remoteVideoZoomChanged
} from '../zoom-sync';

import { receiveAnswer, receivePoll } from './actions';
import {
    COMMAND_ANSWER_POLL,
    COMMAND_NEW_POLL,
    COMMAND_OLD_POLLS
} from './constants';

const parsePollData = pollData => {
    if (typeof pollData !== 'object' || pollData === null) {
        return null;
    }
    const { id, senderId, senderName, senderSource, question, answers, pollType } = pollData;

    if (
        typeof id !== 'string'
        || typeof senderId !== 'string'
        || typeof senderName !== 'string'
        || typeof question !== 'string'
        || !(answers instanceof Array)
    ) {
        return null;
    }

    const answersParsed = [];

    // Collect per-voter source so visitor votes stay distinguishable from regular votes in the
    // results UI. Voter records may be a plain name string (legacy) or an object carrying a source.
    const voterSources = {};

    for (const answer of answers) {
        const voters = {};

        for (const [ voterId, voter ] of Object.entries(answer.voters)) {
            if (typeof voter === 'string') {
                voters[voterId] = voter;
                voterSources[voterId] = normalizeMessageSource();
            } else if (voter && typeof voter === 'object') {
                // New shape: { name, source }.
                voters[voterId] = voter.name;
                voterSources[voterId] = normalizeMessageSource(voter.source);
            } else {
                return null;
            }
        }

        answersParsed.push({
            name: answer.name,
            voters
        });
    }

    return {
        changingVote: false,
        senderId,
        senderName,
        senderSource: normalizeMessageSource(senderSource),
        voterSources,
        question,
        pollType: pollType || 'multi-select',
        showResults: false,
        lastVote: null,
        answers: answersParsed
    };
};

let _pollsReceiveMessage = null;

StateListenerRegistry.register(
    state => getCurrentConference(state),
    (conference, store, previousConference) => {
        if (previousConference && _pollsReceiveMessage) {
            previousConference.off(
                MHConferenceEvents.ENDPOINT_MESSAGE_RECEIVED,
                _pollsReceiveMessage
            );
            previousConference.off(
                MHConferenceEvents.NON_PARTICIPANT_MESSAGE_RECEIVED,
                _pollsReceiveMessage
            );
            _pollsReceiveMessage = null;
        }
        if (conference && conference !== previousConference) {
            const receiveMessage = (_, data) => {
                // Add validation for incoming message data
                if (!data || typeof data !== 'object') {
                    logger.warn('Invalid message data received');

                    return;
                }

                switch (data.type) {
                case COMMAND_NEW_POLL: {
                    const { question, answers, pollId, senderId, senderName, senderSource, pollType } = data;

                    // Validate required fields
                    if (!question || !answers || !pollId || !senderId || !senderName) {
                        logger.warn('Invalid poll data received');
                        break;
                    }

                    const poll = {
                        changingVote: false,
                        senderId,
                        senderName,

                        // Origin of the poll sender. Missing source is treated as main-room.
                        senderSource: normalizeMessageSource(senderSource),
                        voterSources: {},
                        showResults: false,
                        lastVote: null,
                        question,
                        pollType: pollType || 'multi-select', // Default to multi-select for backward compatibility
                        answers: answers.map(answer => {
                            return {
                                name: answer,
                                voters: {}
                            };
                        })
                    };

                    store.dispatch(receivePoll(pollId, poll, true));

                    if (checkIfReactNative()) {
                        store.dispatch(showNotification({ titleKey: 'polls.notification.title' }));
                    } else {
                        store.dispatch(
                        showNotification(
                            {
                                appearance: NOTIFICATION_TYPE.NORMAL,
                                titleKey: 'polls.notification.title',
                                customActionNameKey: 'polls.notification.description',
                                customActionHandler: () => {
                                    store.dispatch(checkIfReactNative() ? openPolls() : openChat());
                                    store.dispatch(setIsPollsTabFocused(true));
                                    store.dispatch(close());

                                    return true;
                                }
                            },
                            NOTIFICATION_TIMEOUT_TYPE.STICKY
                        )
                        );
                    }
                    break;
                }

                case CONFERENCE_SUBJECT: {
                    const { subject, senderId } = data;

                    if (!subject || !senderId) {
                        logger.warn('CONFERENCE_SUBJECT: Missing subject or senderId');
                        break;
                    }
                    store.dispatch(conferenceSubjectHandler(subject));
                    break;
                }

                case EXTEND_CONFERENCE_EXPIRY: {
                    const { extendConferenceExpiry } = data;

                    store.dispatch({ type: EXTEND_CONFERENCE_EXPIRY,
                        extendConferenceExpiry });

                    break;
                }

                case IS_CUSTOM_PARTICIPANT_EVERYONE: {
                    const { action } = data;

                    store.dispatch(setCustomParticipantEveryone(action));
                    break;
                }

                case 'MODERATOR_ZOOM_SYNC': {
                    const { participantId, videoType, zoomState } = data;

                    if (participantId && videoType && zoomState) {
                        const parsedState = parseZoomCommand(zoomState);

                        if (parsedState && !isSameZoomState(getRemoteVideoZoom(store.getState(), participantId, videoType), parsedState)) {
                            store.dispatch(remoteVideoZoomChanged(participantId, videoType, parsedState));
                        }
                    }
                    break;
                }

                case SET_MODERATOR_PIN: {
                    const { participant_id, senderId, action: pinAction = 'pin' } = data;

                    if (!participant_id) {
                        logger.warn('SET_MODERATOR_PIN: Missing participant_id');
                        break;
                    }

                    // Dispatch the moderator pin action
                    store.dispatch(setModeratorPin(participant_id, pinAction));
                    store.dispatch(selectParticipantInLargeVideo(participant_id));

                    // Also dispatch regular pin action to maintain consistency
                    if (pinAction === 'pin') {
                        store.dispatch(pinParticipant(participant_id));
                    } else if (pinAction === 'unpin') {
                        store.dispatch(remoteVideoZoomChanged(participant_id, VIDEO_TYPE.CAMERA, IDENTITY_ZOOM_STATE));
                        store.dispatch(remoteVideoZoomChanged(participant_id, VIDEO_TYPE.DESKTOP, IDENTITY_ZOOM_STATE));
                    }

                    // Show notification to all participants except the sender
                    try {
                        const currentConference = getCurrentConference(store.getState());
                        const myId = currentConference && typeof currentConference.myUserId === 'function'
                            ? currentConference.myUserId()
                            : undefined;

                        if (!myId || senderId !== myId) {
                            const notificationKey = pinAction === 'pin'
                                ? 'videothumbnail.pinByModerator'
                                : 'videothumbnail.unpinByModerator';
                            const notificationDescriptionKey = pinAction === 'pin'
                                ? 'videothumbnail.moderatorPinDiscription'
                                : 'videothumbnail.unpinByModerator';

                            store.dispatch(
                                showNotification(
                                    {
                                        appearance: NOTIFICATION_TYPE.NORMAL,
                                        titleKey: notificationKey,
                                        descriptionKey: notificationDescriptionKey
                                    },
                                    3000
                                )
                            );
                        }
                    } catch {
                        // no-op
                    }

                    break;
                }

                case PARTICIPANT_DESIGNATION: {
                    const { participant_id, designation, organization } = data;

                    if (!participant_id || !designation) {
                        logger.warn('PARTICIPANT_DESIGNATION: Missing participant_id or designation');
                        break;
                    }

                    store.dispatch(participantDesignation(participant_id, designation));
                    store.dispatch(participantOrganization(participant_id, organization));
                    break;
                }

                case PARTICIPANT_COMPANY: {
                    const { participant_id, company } = data;

                    if (!participant_id || !company) {
                        logger.warn('PARTICIPANT_COMPANY: Missing participant_id or company');
                        break;
                    }

                    store.dispatch(participantOrganization(participant_id, company));
                    break;
                }

                case PIN_PARTICIPANT: {
                    const { participant_id, pinType = 'self' } = data;

                    if (!participant_id) {
                        logger.warn('PIN_PARTICIPANT: Missing participant_id');
                        break;
                    }

                    store.dispatch(pinParticipant(participant_id, pinType));
                    break;
                }

                case COMMAND_ANSWER_POLL: {
                    const { pollId, answers, voterId, voterName, senderSource } = data;

                    if (!pollId || !answers || !voterId || !voterName) {
                        logger.warn('Invalid poll answer data received');
                        break;
                    }

                    const receivedAnswer = {
                        voterId,
                        voterName,

                        // Origin of the voter. Missing source is treated as main-room.
                        source: normalizeMessageSource(senderSource),
                        pollId,
                        answers
                    };

                    store.dispatch(receiveAnswer(pollId, receivedAnswer));
                    break;
                }

                case DELETE_MESSAGE: {
                    const { messageId, deletedAt } = data;

                    if (!messageId) {
                        logger.warn('DELETE_MESSAGE: Missing messageId');
                        break;
                    }

                    store.dispatch(deleteChatMessage(messageId, deletedAt));
                    break;
                }

                case RESTORE_MESSAGE: {
                    const { messageId } = data;

                    if (!messageId) {
                        logger.warn('RESTORE_MESSAGE: Missing messageId');
                        break;
                    }

                    store.dispatch(restoreChatMessage(messageId));
                    break;
                }

                case MODERATOR_UNLOCK_AUDIO: {
                    store.dispatch(moderatorunclockAudio());
                    break;
                }

                case MODERATOR_UNLOCK_VIDEO: {
                    store.dispatch(moderatorunclockVideo());
                    break;
                }

                case 'SET_PRIVATE_CHAT_DISABLED': {
                    const { disabled } = data;

                    store.dispatch(setPrivateChatDisabled(disabled));
                    break;
                }

                // Read, and sender-validated, by the Focus Mode middleware off the
                // ENDPOINT_MESSAGE_RECEIVED redux action.
                case SET_FOCUS_MODE_MESSAGE_TYPE:
                    break;

                case COMMAND_OLD_POLLS: {
                    const { polls } = data;

                    if (!polls || !Array.isArray(polls)) {
                        logger.warn('Invalid old polls data received');
                        break;
                    }

                    for (const pollData of polls) {
                        const poll = parsePollData(pollData);

                        if (poll === null) {
                            logger.warn('Invalid old poll data');
                        } else {
                            store.dispatch(receivePoll(pollData.id, poll, false));
                        }
                    }
                    break;
                }

                default:
                    logger.warn('Unknown message type received:', data.type);
                    break;
                }
            };

            // Register message listeners
            _pollsReceiveMessage = receiveMessage;

            conference.on(
                MHConferenceEvents.ENDPOINT_MESSAGE_RECEIVED,
                receiveMessage
            );
            conference.on(
                MHConferenceEvents.NON_PARTICIPANT_MESSAGE_RECEIVED,
                receiveMessage
            );
        }
    }
);
